import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Phone, MapPin } from "lucide-react";
import { SocialLinks } from "./Socials";

const links = [
  { label: "Home", href: "#top" },
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Gallery", href: "#portfolio" },
  { label: "Reviews", href: "#testimonials" },
  { label: "Contact", href: "#contact" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => {
    setOpen(false);
    document.querySelector(href)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <>
      {/* Top contact bar */}
      <div className="hidden md:block bg-heirloom-maroon-deep text-heirloom-bone/90">
        <div className="max-w-[1300px] mx-auto px-6 h-10 flex items-center justify-between text-xs tracking-wide">
          <div className="flex items-center gap-6">
            <span className="flex items-center gap-2"><MapPin size={13} className="text-heirloom-gold-light" /> Orbit Mall, Vijay Nagar, Indore</span>
            <a href="tel:+918770847087" className="flex items-center gap-2 hover:text-heirloom-gold-light transition-colors"><Phone size={13} className="text-heirloom-gold-light" /> +91 87708 47087</a>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-heirloom-gold-light">Trusted Wedding Planners Since 2011</span>
            <SocialLinks size={15} itemClass="hover:text-heirloom-gold-light transition-colors" />
          </div>
        </div>
      </div>

      {/* Main nav */}
      <motion.header
        data-testid="site-nav"
        className={`fixed left-0 right-0 z-50 transition-all duration-500 ${
          scrolled
            ? "top-0 bg-heirloom-bone/95 backdrop-blur-md shadow-[0_6px_30px_-12px_rgba(0,0,0,0.25)]"
            : "md:top-10 top-0 bg-gradient-to-b from-black/60 via-black/25 to-transparent"
        }`}
      >
        <div className="max-w-[1300px] mx-auto px-6 h-20 flex items-center justify-between">
          <a href="#top" onClick={(e) => { e.preventDefault(); go("#top"); }} data-testid="nav-logo" className="flex items-center group">
            <img src="/arihant-logo.jpeg" alt="Arihant Events logo" className="h-12 md:h-14 w-auto rounded-xl shadow-md" />
          </a>

          <nav className="hidden lg:flex items-center gap-8">
            {links.map((l) => (
              <button
                key={l.href}
                data-testid={`nav-${l.label.toLowerCase()}`}
                onClick={() => go(l.href)}
                className={`text-sm font-medium transition-colors relative after:absolute after:left-0 after:-bottom-1 after:h-[2px] after:w-0 hover:after:w-full after:bg-heirloom-gold after:transition-all after:duration-300 ${
                  scrolled ? "text-heirloom-obsidian/80 hover:text-heirloom-maroon" : "text-heirloom-bone/90 hover:text-heirloom-gold-light"
                }`}
              >
                {l.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <button
              data-testid="nav-inquire-btn"
              onClick={() => go("#inquiry")}
              className="hidden sm:inline-flex bg-heirloom-maroon text-heirloom-bone text-xs font-semibold tracking-[0.15em] uppercase px-6 py-3.5 rounded-full hover:bg-heirloom-maroon-deep hover:shadow-lg transition-all duration-300"
            >
              Book Now
            </button>
            <button data-testid="nav-mobile-toggle" className={`lg:hidden ${scrolled ? "text-heirloom-maroon" : "text-heirloom-bone"}`} onClick={() => setOpen(true)}>
              <Menu size={26} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ x: "100%" }} animate={{ x: 0 }} exit={{ x: "100%" }}
            transition={{ type: "tween", duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="fixed inset-0 z-[60] bg-heirloom-maroon-deep text-heirloom-bone flex flex-col p-8"
            data-testid="mobile-menu"
          >
            <div className="flex justify-between items-center">
              <span className="font-display text-2xl">Arihant Events</span>
              <button data-testid="mobile-menu-close" onClick={() => setOpen(false)}><X size={28} /></button>
            </div>
            <div className="flex-1 flex flex-col justify-center gap-5">
              {links.map((l, i) => (
                <motion.button key={l.href} initial={{ x: 30, opacity: 0 }} animate={{ x: 0, opacity: 1 }} transition={{ delay: 0.1 + i * 0.06 }} onClick={() => go(l.href)} className="font-display text-4xl text-left hover:text-heirloom-gold-light transition-colors">
                  {l.label}
                </motion.button>
              ))}
            </div>
            <button onClick={() => go("#inquiry")} className="bg-heirloom-gold text-heirloom-maroon-deep font-semibold tracking-widest uppercase text-sm py-4 rounded-full">Book Now</button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
